import { supabase } from '../lib/supabase'
import { checkLimit } from './usageService'

const MAX_FILE_SIZE = parseInt(import.meta.env.VITE_MAX_FILE_SIZE) || 52428800 // 50MB default
const ALLOWED_TYPES = import.meta.env.VITE_ALLOWED_FILE_TYPES?.split(',') || []

// Validate file
export const validateFile = (file) => {
  if (file.size > MAX_FILE_SIZE) {
    throw new Error(`File size must be less than ${MAX_FILE_SIZE / 1024 / 1024}MB`)
  }

  if (ALLOWED_TYPES.length > 0 && !ALLOWED_TYPES.includes(file.type)) {
    throw new Error('File type not allowed')
  }

  return true
}

// Upload file to Supabase Storage
export const uploadFile = async (file, projectId) => {
  validateFile(file)

  // Get organization_id from project for secure file path
  const { data: project, error: projectError } = await supabase
    .from('projects')
    .select('organization_id')
    .eq('id', projectId)
    .single()

  if (projectError) throw projectError

  const fileExt = file.name.split('.').pop()
  // Secure path: organizationId/projectId/timestamp_uuid.ext
  const fileName = `${project.organization_id}/${projectId}/${Date.now()}_${crypto.randomUUID()}.${fileExt}`

  const { data, error } = await supabase.storage
    .from('project-files')
    .upload(fileName, file, {
      cacheControl: '3600',
      upsert: false
    })

  if (error) throw error
  return data
}

// Create project version
export const createVersion = async (versionData) => {
  // Get project's organization_id to check storage limit
  const { data: project } = await supabase
    .from('projects')
    .select('organization_id')
    .eq('id', versionData.project_id)
    .single()

  if (project) {
    // Check storage limit before creating version
    const limitCheck = await checkLimit(project.organization_id, 'storage')
    if (!limitCheck.allowed) {
      const error = new Error(limitCheck.message)
      error.code = 'LIMIT_REACHED'
      error.limitType = 'storage'
      throw error
    }

    // Show warning if approaching limit
    if (limitCheck.message) {
      console.warn(limitCheck.message)
    }
  }

  const { data, error } = await supabase
    .from('project_versions')
    .insert([versionData])
    .select()
    .single()

  if (error) throw error
  return data
}

// Get version
export const getVersion = async (versionId) => {
  const { data, error } = await supabase
    .from('project_versions')
    .select(`
      *,
      project:projects(*),
      comments:comments(
        *,
        user:users(email, full_name)
      ),
      approvals:approvals(
        *,
        user:users(email, full_name)
      )
    `)
    .eq('id', versionId)
    .single()

  if (error) throw error
  return data
}

// Get project versions
export const getProjectVersions = async (projectId) => {
  const { data, error } = await supabase
    .from('project_versions')
    .select('*')
    .eq('project_id', projectId)
    .order('version_number', { ascending: false })

  if (error) throw error
  return data
}

// Get signed URL for file
export const getFileUrl = async (filePath, expiresIn = 3600) => {
  const { data, error } = await supabase.storage
    .from('project-files')
    .createSignedUrl(filePath, expiresIn)

  if (error) throw error
  return data.signedUrl
}

// Delete file
export const deleteFile = async (filePath) => {
  const { error } = await supabase.storage
    .from('project-files')
    .remove([filePath])

  if (error) throw error
}
